"use client";

import { User } from "@supabase/supabase-js";
import { createClient } from "@/libs/supabase/client";
import { useEffect, useState, ReactNode } from "react";
import { usePathname } from "next/navigation";
import dynamic from "next/dynamic";
import NextTopLoader from "nextjs-toploader";
import { Toaster } from "react-hot-toast";
import ErrorBoundary from "@/components/ErrorBoundary";
import config from "@/config";

const Tooltip = dynamic(
  () => import('react-tooltip').then(mod => ({ default: mod.Tooltip })),
  { ssr: false }
);

const ChatWidget = dynamic(
  () => import('@/components/ChatWidget'),
  { ssr: false }
);

// Crisp customer chat support:
// This component is separated from ClientLayout because it needs to be wrapped with <SessionProvider> to use useSession() hook
const CrispChat = (): null => {
  const pathname = usePathname();

  const supabase = createClient();
  const [data, setData] = useState<{ user: User }>(null);

  // Skip Crisp and Supabase auth on blog pages to reduce client JS overhead
  const isBlogPage = pathname?.startsWith("/blog");

  // This is used to get the user data from Supabase Auth (if logged in) => user ID is used to identify users in Crisp.
  // Skip entirely when Crisp is disabled (no id) so we don't fire a needless auth network call on every page.
  useEffect(() => {
    if (isBlogPage || !config?.crisp?.id) return;

    const getUser = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (user) {
        setData({ user });
      }
    };
    getUser();
  }, [isBlogPage]);

  useEffect(() => {
    if (isBlogPage || !config?.crisp?.id) return;

    import('crisp-sdk-web').then(({ Crisp }) => {
      Crisp.configure(config.crisp.id);

      if (
        config.crisp.onlyShowOnRoutes &&
        !config.crisp.onlyShowOnRoutes?.includes(pathname)
      ) {
        Crisp.chat.hide();
        Crisp.chat.onChatClosed(() => {
          Crisp.chat.hide();
        });
      }
    });
  }, [pathname, isBlogPage]);

  // Add User Unique ID to Crisp to easily identify users when reaching support (optional)
  useEffect(() => {
    if (data?.user && config?.crisp?.id) {
      import('crisp-sdk-web').then(({ Crisp }) => {
        Crisp.session.setData({ userId: data.user?.id });
      });
    }
  }, [data]);

  return null;
};

// Defer mounting the floating AI chat widget until the browser is idle so it
// doesn't add work to initial hydration (helps INP). Same pattern as PostHogInit.
const DeferredChatWidget = (): JSX.Element | null => {
  const [ready, setReady] = useState(false);
  const pathname = usePathname();

  // Don't mount the floating chat on auth pages. On the sign-in screen people
  // confuse it with the code-entry box and paste their OTP into it.
  const isAuthRoute = !!pathname && (pathname === "/signin" || pathname.startsWith("/signin"));

  useEffect(() => {
    if (isAuthRoute) return;
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => setReady(true), { timeout: 3000 });
    } else {
      setTimeout(() => setReady(true), 2000);
    }
  }, [isAuthRoute]);

  if (isAuthRoute) return null;
  return ready ? <ChatWidget /> : null;
};

// All the client wrappers are here (they can't be in server components)
// 1. NextTopLoader: Show a progress bar at the top when navigating between pages
// 2. Toaster: Show Success/Error messages anywhere from the app with toast()
// 3. Tooltip: Show tooltips if any JSX elements has these 2 attributes: data-tooltip-id="tooltip" data-tooltip-content=""
// 4. CrispChat: Set Crisp customer chat support (see above)
const ClientLayout = ({ children }: { children: ReactNode }) => {
  return (
    <>
      {/* Show a progress bar at the top when navigating between pages */}
      <NextTopLoader color={config.colors.main} showSpinner={false} />

      {/* ============ INCIDENT BANNER ============
          Set to true to show, false to hide.
          Change the message as needed.
      */}
      {true && (
        <div className="bg-base-300 text-base-content px-4 py-1.5 text-center text-xs font-medium border-b border-base-content/10">
          Users are responsible for the legal and ethical use of our outputs.
        </div>
      )}

      {/* Content inside app/page.js files  */}
      {children}

      {/* Show Success/Error messages anywhere from the app with toast() */}
      <Toaster
        toastOptions={{
          duration: 3000,
        }}
      />

      {/* Show tooltips if any JSX elements has these 2 attributes: data-tooltip-id="tooltip" data-tooltip-content="" */}
      {/* Boundaries: these widgets render outside app/error.tsx's jurisdiction — an unhandled throw here crashes the whole root layout (React #306 from a stale lazy chunk was the 2026-09-13 incident) */}
      <ErrorBoundary fallback={null} reportContext="client.widget.tooltip">
        <Tooltip
          id="tooltip"
          className="z-[60] !opacity-100 max-w-sm shadow-lg"
        />
      </ErrorBoundary>

      {/* Set Crisp customer chat support */}
      <CrispChat />

      {/* AI chat assistant (deferred until idle) */}
      <ErrorBoundary fallback={null} reportContext="client.widget.chat">
        <DeferredChatWidget />
      </ErrorBoundary>
    </>
  );
};

export default ClientLayout;
